import { useEffect, useRef } from 'react';
import { animate, useInView, useReducedMotion } from 'motion/react';
import type { AppLanguage } from '../../../i18n/config';
import { formatInteger } from '../../../i18n/formatNumber';

interface CountUpProps {
    to: number;
    durationSeconds?: number;
    className?: string;
    language?: AppLanguage;
    prefix?: string;
    suffix?: string;
}

const EASE_OUT = [0.16, 1, 0.3, 1] as const;

function formatCount(value: number, language: AppLanguage | undefined, prefix: string, suffix: string): string {
    const digits = language ? formatInteger(value, language) : String(Math.round(value));
    return `${prefix}${digits}${suffix}`;
}

export default function CountUp({ to, durationSeconds = 1.6, className, language, prefix = '', suffix = '' }: CountUpProps) {
    const ref = useRef<HTMLSpanElement>(null);
    const isInView = useInView(ref, { once: true, margin: '0px 0px -15% 0px' });
    const prefersReducedMotion = useReducedMotion();

    useEffect(() => {
        if (prefersReducedMotion || !ref.current) return;
        ref.current.textContent = formatCount(0, language, prefix, suffix);
    }, [prefersReducedMotion, language, prefix, suffix]);

    useEffect(() => {
        if (!isInView || prefersReducedMotion) return;
        const controls = animate(0, to, {
            duration: durationSeconds,
            ease: EASE_OUT,
            onUpdate: (latest) => {
                if (ref.current) ref.current.textContent = formatCount(latest, language, prefix, suffix);
            }
        });
        return () => controls.stop();
    }, [isInView, prefersReducedMotion, to, durationSeconds, language, prefix, suffix]);

    return <span ref={ref} className={className}>{formatCount(to, language, prefix, suffix)}</span>;
}
